/**
 * Pinned documents (card 1.4, data part): an ordered `pinnedDocumentIds` list
 * in a `docprefs` record of extension_documents, one per scope bucket, so pins
 * sync across devices exactly like the documents they point at.
 *
 * - Personal / device bucket: record id `prefs` (the bucket is the user's own).
 * - Team bucket: record id `prefs:<userId>` — a team bucket is shared, and a
 *   pin is one person's shortcut, not the team's.
 *
 * User workspaces (card 5.1) share the personal bucket, so their pins share
 * one list; `listPins(scope, nodes)` keeps only ids present in the tree the
 * caller shows — which is also how a deleted document drops out silently.
 *
 * `data` is read back and carried forward on every write (the upsert replaces
 * it wholesale), so a later prefs field survives a pin toggle.
 */
import { listRecords, upsertRecord } from '../../../records/recordsClient';
import { useAuthStore } from '../../../store/authStore';
import { padKey, recordsContext } from './documentsStore';
import { dataOf } from './documentsScopeTree';

const EXTENSION_ID = 'documents';
const RECORD_TYPE = 'docprefs';
/** A pin list is a sidebar section, not an archive; the oldest fall off past this. */
export const MAX_PINS = 200;

function prefsId(scope) {
  if (scope?.type !== 'team') return 'prefs';
  const userId = useAuthStore.getState()?.user?.id;
  return userId != null ? `prefs:${userId}` : 'prefs:local';
}

async function readPrefs(scope) {
  const ctx = recordsContext(scope);
  const id = prefsId(scope);
  const rows =
    (await listRecords({ workspaceId: ctx.workspaceId, extensionId: EXTENSION_ID, recordType: RECORD_TYPE, scopeId: null })) || [];
  const row = rows.find((r) => String(r.id) === id);
  const data = row ? dataOf(row) : {};
  const ids = Array.isArray(data.pinnedDocumentIds) ? data.pinnedDocumentIds.map(String) : [];
  return { ctx, id, data, ids: [...new Set(ids)] };
}

async function writePins({ ctx, id, data }, ids) {
  const pinnedDocumentIds = [...new Set(ids.map(String))].slice(-MAX_PINS);
  await upsertRecord({
    workspaceId: ctx.workspaceId,
    extensionId: EXTENSION_ID,
    record: { id, record_type: RECORD_TYPE, parent_id: null, sort_key: padKey(0), data: { ...data, pinnedDocumentIds } },
  });
  return pinnedDocumentIds;
}

/**
 * The pinned document ids, in the user's order. With `nodes` (a `listTree`
 * result), only ids that are documents in that tree — so a deleted page, or a
 * page of another workspace, is not shown.
 */
export async function listPins(scope, nodes = null) {
  const { ids } = await readPrefs(scope);
  if (!nodes) return ids;
  const docs = new Set(nodes.filter((n) => n.type === 'document').map((n) => String(n.id)));
  return ids.filter((id) => docs.has(id));
}

export async function isPinned(docId, scope) {
  return (await readPrefs(scope)).ids.includes(String(docId));
}

/** Pins (appended at the end) or unpins `docId`. Resolves the new state: true = pinned. */
export async function togglePin(docId, scope) {
  const prefs = await readPrefs(scope);
  const id = String(docId);
  const pinned = prefs.ids.includes(id);
  await writePins(prefs, pinned ? prefs.ids.filter((x) => x !== id) : [...prefs.ids, id]);
  return !pinned;
}

/**
 * Puts the pins in `orderedIds` order (drag within Pinned). Ids not pinned are
 * ignored; pinned ids the caller did not list (another workspace's) keep their
 * relative order after them. Resolves the stored list.
 */
export async function reorderPins(orderedIds, scope) {
  const prefs = await readPrefs(scope);
  const wanted = [...new Set((orderedIds || []).map(String))].filter((id) => prefs.ids.includes(id));
  return writePins(prefs, [...wanted, ...prefs.ids.filter((id) => !wanted.includes(id))]);
}
